
import { defineComponent, ref } from "vue";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ListAutoDispose } from "@amcharts/amcharts5/.internal/core/util/List";
import { Color } from "@amcharts/amcharts5/.internal/core/util/Color";
import am5lang_es_ES from "@amcharts/amcharts5/locales/es_ES";
// import SamplingPointImageComponent from "@/components/samplingPoints/SamplingPointImage.component.vue";
import { i18n } from "@/plugins/i18n.plugin";
import { SateliteImage } from "@/types/satelitalSamplingPoint.type";
import SatelitalImageModalComponent from "./SatelitalImageModal.component.vue";
import { getSatelitalImgByDate } from "@/service/satelital.service";

interface DataChart {
  date: number;
  value: number | undefined;
  unit?: string;
  fileName?: string;
  bulletSettings?: { fill: Color };
  strokeSettings?: { stroke: Color };
}

interface DataContext {
  value: number;
}

export default defineComponent({
  components: {
    // SamplingPointImageComponent,
    SatelitalImageModalComponent,
  },
  props: {
    parameterName: { type: String, required: true },
    dataCollection: {
      type: Array as () => Array<{
        date: number;
        value: number | undefined;
        altValue: any;
      }>,
      required: true,
    },
    satelitalSamplingPointId: { type: Number, required: true, immediate: true, deep: true },
  },
  mounted() {
    this.fillData([]);
  },
  setup() {
    const chart = "" as unknown as am5xy.XYChart;
    // Leyenda
    const legend = "" as unknown as am5.Legend;
    // Id grafica
    const chartId = "chartdiv";
    // Datos de los parámetros para graficar
    const series = "" as unknown as am5xy.LineSeries;
    // Visibilidad del modal de imagen
    const visible = ref(false);

    return { chart, legend, chartId, series, visible };
  },
  data() {
    return {
      // lastImage: undefined as Image | undefined,
      // fileNames: [] as unknown as string[],
      // currentIndex: undefined as number | undefined,
      maxValue: undefined as number | undefined,
      minValue: undefined as number | undefined,
      imgVisible: false as boolean,
      image: {} as SateliteImage,
    };
  },

  watch: {
    dataCollection: async function () {
      if (this.dataCollection && this.dataCollection.length > 0) {
        // if (!this.chart.yAxes.getIndex(0)?.get("visible")) {
        //   this.chart.yAxes.getIndex(0)?.set("visible", true);
        // }
      } else {
        if (this.chart && this.chart.root) {
          this.chart.root.dispose();
          this.fillData([]);
        }
      }
      this.updateChartData();
      this.createLegend(this.chart.root);
    },
  },
  methods: {
    /**
     * Generar gráfica
     * @param dataCollection
     */
    async fillData(dataCollection: DataChart[]) {
      let root = am5.Root.new("chartdiv");
      root.locale = am5lang_es_ES;
      // Set themes
      root.setThemes([am5themes_Animated.new(root)]);

      // Create chart
      this.chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          focusable: true,
          panX: true,
          panY: true,
          wheelX: "panX",
          wheelY: "zoomX",
          paddingLeft: 0,
          paddingBottom: 40,
        })
      );

      let cursor = this.chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineX.set("visible", true);
      cursor.lineY.set("visible", false);

      this.chart.get("colors")?.set("step", 2);

      // Create axes
      const xAxis = this.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          maxDeviation: 0,
          groupData: false,
          baseInterval: {
            timeUnit: "minute",
            count: 1,
          },
          renderer: am5xy.AxisRendererX.new(root, {
            minorGridEnabled: true,
          }),
          //tooltip: am5.Tooltip.new(root, {}),
        })
      );

      this.createAxisYParameter(root, xAxis, dataCollection);

      this.series.appear(1000);
      this.chart.appear(1000, 100);
    },

    /**
     *
     * @param root
     * @param xAxis
     * @param dataCollection
     */
    createAxisYParameter(
      root: am5.Root,
      xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
      dataCollection: DataChart[]
    ) {
      // -- export
      var yRenderer_1 = am5xy.AxisRendererY.new(root, {
        opposite: false,
      });
      var yAxis = this.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer_1,
        })
      );

      if (this.chart.yAxes.indexOf(yAxis) > 0) {
        yAxis.set(
          "syncWithAxis",
          (this.chart.yAxes as ListAutoDispose<am5xy.ValueAxis<am5xy.AxisRenderer>>).getIndex(0)
        );
      }

      this.series = this.chart.series.push(
        am5xy.LineSeries.new(root, {
          name: `${this.parameterName}`,
          xAxis: xAxis,
          yAxis: yAxis,
          valueYField: "value",
          valueXField: "date",
        })
      );

      yRenderer_1.grid.template.set("strokeOpacity", 0.05);
      yRenderer_1.labels.template.set("fill", this.series.get("fill"));
      // Eje y
      yRenderer_1.setAll({
        stroke: this.series.get("fill"), //Color del relleno
        strokeOpacity: 1,
        opacity: 1,
      });

      this.series.bullets.push((root, _, dataItem) => {
        // Obtener el valor actual del bullet
        let dataContext = dataItem.dataContext as DataContext;
        let data = dataItem.dataContext as {
          date: number;
          value: number | undefined;
          altValue: any;
        };
        let value = dataContext.value;

        let opacity = 0;
        let circleColor = this.series.get("fill");
        let radius = 3;
        if (value === this.minValue) {
          circleColor = am5.color(0x50c878);
          opacity = 1;
        } else if (value === this.maxValue) {
          circleColor = am5.color(0xffa500);
          opacity = 1;
        }

        if (data.altValue == null) {
          dataItem.set("valueY", 0);
          circleColor = am5.color(0xbbbbbb);
          opacity = 1;
          radius = 4;
          value = 0;
        }
        let bulletCircle = am5.Circle.new(root, {
          radius: radius,
          fill: circleColor,
          opacity: opacity,
          cursorOverStyle: "pointer",
          tooltipHTML: `
            <div style="display: flex; justify-content: center; align-items: center; height: 100%; text-align: center;">
              <div>
                <div>{valueX.formatDate('dd/MM/yyyy HH:mm')}</div>
                <div style="font-weight: bold;">${
                  value == 0 && data.altValue == null
                    ? "No procesado"
                    : 'Valor: {valueY.formatNumber("#,###.##")}'
                }</div>
              </div>
            </div>
          `,
        });

        let container: any = am5.Container.new(root, {});

        container.children.push(bulletCircle);
        container.events.on("click", async (event: any) => {
          this.imgVisible = true;

          const dataItem = event.target.dataItem;

          const rawDate = new Date(dataItem.dataContext.date);
          const formattedDateTime = rawDate.toISOString().replace("T", " ").split(".")[0];
          this.image = await getSatelitalImgByDate(
            formattedDateTime,
            this.satelitalSamplingPointId
          );
        });
        return am5.Bullet.new(root, {
          sprite: container,
        });
      });

      if (this.dataCollection) {
        this.addScrollbar(this.chart, root, this.dataCollection);
      }

      this.series.fills.template.setAll({ fillOpacity: 0.3, visible: true });
      return yAxis;
    },

    /**
     * Crear leyenda de la gráfica
     * @param root
     */
    createLegend(root: am5.Root) {
      this.legend = this.chart.children.push(
        am5.Legend.new(root, {
          x: am5.percent(33),
          centerX: am5.percent(33),
          y: am5.p100,
          layout: root.horizontalLayout,
        })
      );

      if (this.chart.series.values) {
        this.legend.data.setAll(this.chart.series.values);
      }

      if (root.container.children.length > 1) {
        root.container.children.pop();
      }

      this.createFixedLegend(root);
    },

    createFixedLegend(root: am5.Root) {
      this.legend.data.setAll([]);
      let data = am5.Container.new(root, {
        layout: root.horizontalLayout,
        x: am5.percent(90),
        centerX: am5.percent(100),
        y: am5.percent(101),
        centerY: am5.percent(101),
        paddingTop: 20,
        paddingLeft: 20,
      });

      if (root.container.children.length > 1) {
        root.container.children.pop();
      }
      let legendContainer = root.container.children.push(data);

      let minItem = this.createLegendItemDot(
        root,
        // `Mín: ${this.minValue ? i18n.global.n(this.minValue, "rounded") : "-"}`,
        `Mín: ${this.minValue ? i18n.global.n(this.minValue, "fixed") : "-"}`,
        am5.color(0x50c878)
      );
      legendContainer.children.push(minItem);

      let maxItem = this.createLegendItemDot(
        root,
        // `Max: ${this.maxValue ? i18n.global.n(this.maxValue, "rounded") : "-"}`,
        `Max: ${this.maxValue ? i18n.global.n(this.maxValue, "fixed") : "-"}`,
        am5.color(0xffa500)
      );
      legendContainer.children.push(maxItem);
      let invalidItem = this.createLegendItemDot(root, "No procesado", am5.color(0xbbbbbb));
      legendContainer.children.push(invalidItem);
    },

    createLegendItemDot(root: am5.Root, name: string, color: am5.Color) {
      let legendItem = am5.Container.new(root, {
        layout: root.horizontalLayout,
        marginBottom: 0,
        marginRight: 10,
        centerY: am5.percent(50),
      });

      let colorDot = am5.Circle.new(root, {
        radius: 5,
        fill: color,
        marginRight: 5,
        centerY: am5.percent(50),
      });

      let label = am5.Label.new(root, {
        text: name,
        fontSize: 14,
        marginRight: 10,
        centerY: am5.percent(50),
      });

      legendItem.children.push(colorDot);
      legendItem.children.push(label);
      return legendItem;
    },

    updateChartData() {
      const validValues = this.dataCollection
        .map((item) => item.value)
        .filter((value): value is number => value !== undefined && value !== null && value !== 0);

      if (validValues.length > 0) {
        this.minValue = Math.min(...validValues);
        this.maxValue = Math.max(...validValues);
      } else {
        this.minValue = undefined;
        this.maxValue = undefined;
      }

      if (this.series.data) {
        this.series.data.setAll(this.dataCollection);
      }
    },

    addScrollbar(chart: am5xy.XYChart, root: am5.Root, estimation: DataChart[]) {
      // add scrollbar
      var scrollbarX = chart.set(
        "scrollbarX",
        am5xy.XYChartScrollbar.new(root, {
          orientation: "horizontal",
          height: 20,
        })
      );

      var scrollbarY = chart.set(
        "scrollbarY",
        am5.Scrollbar.new(root, {
          orientation: "vertical",
          width: 15,
        })
      );

      scrollbarY?.get("background")?.setAll({
        strokeOpacity: 0,
      });
      var sbxAxis = scrollbarX.chart.xAxes.push(
        am5xy.DateAxis.new(root, {
          groupData: false,
          visible: false,
          baseInterval: { timeUnit: "millisecond", count: 1 },
          renderer: am5xy.AxisRendererX.new(root, {
            minorGridEnabled: false,
          }),
        })
      );

      var yRenderer = am5xy.AxisRendererY.new(root, {});
      yRenderer.grid.template.set("forceHidden", false);

      var sbyAxis = scrollbarX.chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yRenderer,
        })
      );
    },
  },
});
